/**
 * Which app's built-in browser this is, if any.
 *
 * Every paid visitor so far (Oct 2026) opened Kindling inside Facebook's own
 * browser, from an ad, and none from Instagram's. Those browsers matter for
 * three reasons: Google refuses to sign anyone in from one (Error 403
 * disallowed_useragent), what is kept in one is a separate jar from Safari's or
 * Chrome's, and the email a link is sent to opens in the real browser, not
 * back in here. So the save leads with email, and the link carries the
 * identity across (lib/handoff.ts).
 *
 * - Facebook on iOS: `FBAN/FBIOS` and `FBAV/…`; Messenger: `FBAN/MessengerForiOS`.
 * - Facebook on Android: a Chrome WebView (`; wv)`) with `[FB_IAB/FB4A;FBAV/…]`,
 *   which PostHog reports as plain "Chrome".
 * - Instagram on either: `Instagram 345.0.…`.
 * - Anything else that is a webview and not a browser: `other_webview`.
 *
 * Pure, for the test. `standalone` is a home-screen web app, which on iOS can
 * look like a webview from the user agent alone and is not one.
 */
export type InAppBrowser = 'facebook' | 'instagram' | 'other_webview';

export function detectInAppBrowser(ua: string, opts: { standalone?: boolean } = {}): InAppBrowser | null {
  if (!ua) return null;
  if (/\bInstagram\b/.test(ua)) return 'instagram';
  if (/FBAN\/|FBAV\/|FB_IAB\/|FBIOS|FB4A/.test(ua)) return 'facebook';
  if (opts.standalone) return null;
  // other apps that say who they are
  if (/\b(Line|Twitter|LinkedInApp|Snapchat|Pinterest|musical_ly|BytedanceWebview|TikTok|KAKAOTALK|WhatsApp)\b/i.test(ua) && isPhone(ua)) return 'other_webview';
  // Android's WebView marks itself
  if (/Android/.test(ua) && /; wv\)/.test(ua)) return 'other_webview';
  // iOS: every real browser (Safari, Chrome, Firefox, Edge) still says Safari/…; a bare WKWebView does not
  if (/\b(iPhone|iPad|iPod)\b/.test(ua) && /AppleWebKit/.test(ua) && !/Safari\//.test(ua)) return 'other_webview';
  return null;
}

export type Platform = 'ios' | 'android' | 'other';

export function platformOf(ua: string): Platform {
  if (/\b(iPhone|iPad|iPod)\b/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

function isPhone(ua: string): boolean {
  return platformOf(ua) !== 'other';
}

/**
 * An Android link that opens `url` in Chrome rather than in the app it was
 * tapped in. Pure; the fragment is dropped (the intent's own syntax uses it).
 */
export function chromeIntentUrl(url: string): string {
  const u = new URL(url);
  return `intent://${u.host}${u.pathname}${u.search}#Intent;scheme=${u.protocol.replace(':', '')};package=com.android.chrome;end`;
}

/** This browser. Cheap enough to read each time it is asked. */
export function inAppBrowser(): InAppBrowser | null {
  if (typeof navigator === 'undefined') return null;
  let standalone = false;
  try {
    standalone = window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
  } catch { /* not a browser */ }
  return detectInAppBrowser(navigator.userAgent || '', { standalone });
}

export function platform(): Platform {
  return typeof navigator === 'undefined' ? 'other' : platformOf(navigator.userAgent || '');
}

/** The name a person would call the browser they are in, for copy. */
export function inAppName(kind: InAppBrowser): string {
  return kind === 'facebook' ? 'Facebook' : kind === 'instagram' ? 'Instagram' : 'this app';
}

/**
 * Facebook's, Messenger's and Instagram's in-app browsers, which is where every
 * paid visitor arrives. iOS: `[FBAN/FBIOS;…]`, `FBAN/MessengerForiOS`,
 * `Instagram 312.0…`; Android: `[FB_IAB/FB4A;FBAV/…]`. Pure, for the test.
 *
 * Read for two things: the order auth stores its sign-in in (firebase.ts), and
 * a property on the identity events, so PostHog can say whether losing the
 * sign-in is an in-app-browser problem or everybody's.
 */
export function isMetaInAppBrowser(ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : ''): boolean {
  return /\bFBAN\/|\bFBAV\/|\bFB_IAB\/|\bFBIOS\b|\bInstagram\b/.test(ua);
}
